import React, { useState, useEffect, useCallback, useRef } from 'react';
import ProtectedRoute from '../../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import Tree from 'react-d3-tree';
import { AutoComplete } from "primereact/autocomplete";
import employeeService from '../../../services/data/employee.service';
import { Tooltip } from 'primereact/tooltip';
import { Sidebar } from 'primereact/sidebar';
import SkeletonOrgEmp from './skeletonOrgEmp';

// Recursively inject loaded children into the matching node
function insertChildren(tree, nodeId, newChildren) {
    if (tree._id === nodeId) {
        return { ...tree, children: newChildren, _hasUnloaded: false };
    }
    if (!tree.children || tree.children.length === 0) return tree;
    return { ...tree, children: tree.children.map((c) => insertChildren(c, nodeId, newChildren)) };
}

// Convert PrimeReact tree format → react-d3-tree format
function transformNode(node) {
    if (!node) return null;
    if (node.vacant) {
        return {
            name: 'A Preencher',
            _vacant: true,
            _jobposition: node.jobposition || null,
            children: (node.children || []).map(transformNode).filter(Boolean),
        };
    }
    return {
        name: node.data?.title || '',
        _id: node.data?.id,
        _photo: node.data?.photo || null,
        _jobposition: node.jobposition || null,
        _childrenCount: node.childrenCount || 0,
        _hasUnloaded: (node.children?.length ?? 0) < 1 && (node.childrenCount ?? 0) > 0,
        children: (node.children || []).map(transformNode).filter(Boolean),
    };
}

const NODE_W = 160;
const NODE_H = 100;
const HALF_W = NODE_W / 2;
const HALF_H = NODE_H / 2;

export default function EmployeeOrg() {
    const [pageLoad, setPageLoad] = useState(true);
    const [treeData, setTreeData] = useState(null);
    const [selection, setSelection] = useState(null);
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [showSidebar, setShowSidebar] = useState(false);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });

    // Ref callback to center tree once container mounts
    const containerRef = useCallback((el) => {
        if (el) {
            const { width, height } = el.getBoundingClientRect();
            setTranslate({ x: Math.round(width / 5), y: Math.round(height / 2) });
        }
    }, []);

    // Keep clicJobTree in a ref so renderCustomNode doesn't need it as a dep
    const clicJobTreeRef = useRef(null);

    useEffect(() => {
        listTitles();
    }, []);

    function listTitles() {
        employeeService.listEmployeeOrg()
            .then((response) => {
                if (response.length > 0) setTreeData(transformNode(response[0]));
            })
            .catch((err) => console.log(err?.response?.data))
            .finally(() => setPageLoad(false));
    }

    function clicJobTree(id) {
        employeeService.listEmployeeOrg(id)
            .then((response) => {
                if (response.length > 0) {
                    const subtree = transformNode(response[0]);
                    setTreeData((prev) => insertChildren(prev, id, subtree.children || []));
                }
            })
            .catch((err) => console.log(err?.response?.data));
    }

    clicJobTreeRef.current = clicJobTree;

    function getJobTree() {
        const lookup = items.find((v) => v.user === search);
        if (!lookup) return;
        employeeService.listEmployeeOrg(lookup.id)
            .then((response) => {
                if (response.length > 0) setTreeData(transformNode(response[0]));
            })
            .catch((err) => console.log(err?.response?.data));
    }

    function searchItems(event) {
        if (event.query.length > 0) {
            employeeService.autoCompleteEmployee(event.query)
                .then((response) => setItems(response.map((i) => ({ user: i.user, id: i.id }))))
                .catch(console.error);
        }
    }

    function cleanSearch() {
        setSearch('');
        listTitles();
    }

    const renderNode = useCallback(({ nodeDatum }) => {
        const isVacant = nodeDatum._vacant;

        if (isVacant) {
            return (
                <g>
                    <foreignObject
                        width={NODE_W}
                        height={NODE_H}
                        x={-HALF_W}
                        y={-HALF_H}
                        style={{ overflow: 'visible' }}
                    >
                        <div
                            xmlns="http://www.w3.org/1999/xhtml"
                            style={{
                                width: NODE_W,
                                textAlign: 'center',
                                padding: '10px 8px',
                                borderRadius: 10,
                                background: '#fafafa',
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                                border: '2px dashed #bbb', 
                            }}
                            onClick={() => { setSelection(nodeDatum); setShowSidebar(true); }}
                        >
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: '#e5e5e5', margin: '0 auto 6px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#999', fontSize: 14,
                                border: '1px dashed #bbb'
                            }}>
                                <FontAwesomeIcon icon={solid('user-plus')} />
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#999' }}>A Preencher</div>
                            {nodeDatum._jobposition?.title && (
                                <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>
                                    {nodeDatum._jobposition.title}
                                </div>
                            )}
                            <span style={{
                                display: 'inline-block', marginTop: 4,
                                background: '#ffc107', color: '#333',
                                borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 600,
                            }}>
                                Vaga
                            </span>
                        </div>
                    </foreignObject>
                </g>
            );
        }

        return (
            <g>
                <foreignObject
                    width={NODE_W}
                    height={NODE_H}
                    x={-HALF_W}
                    y={-HALF_H}
                    style={{ overflow: 'visible' }}
                >
                    <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                            width: NODE_W,
                            textAlign: 'center',
                            padding: '10px 8px',
                            borderRadius: 10,
                            background: 'white',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
                        }}
                        onClick={() => { setSelection(nodeDatum); setShowSidebar(true); }}
                    >
                        {nodeDatum._photo ? (
                            <img
                                src={nodeDatum._photo}
                                alt={nodeDatum.name}
                                style={{
                                    width: 36, height: 36, borderRadius: '50%',
                                    objectFit: 'cover', display: 'block', margin: '0 auto 6px',
                                }}
                            />
                        ) : (
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: '#7498a9', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 6px', fontSize: 15,
                            }}>
                                <FontAwesomeIcon icon={regular('user')} />
                            </div>
                        )}
                        <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {nodeDatum.name}
                        </div>
                        {nodeDatum._jobposition?.title && (
                            <div style={{ fontSize: 10, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {nodeDatum._jobposition.title}
                            </div>
                        )}
                        {nodeDatum._childrenCount > 0 && (
                            <span style={{
                                display: 'inline-block', marginTop: 4,
                                background: '#7498a9', color: '#fff',
                                borderRadius: 10, padding: '1px 7px', fontSize: 10,
                            }}>
                                {nodeDatum._childrenCount}
                            </span>
                        )}
                    </div>
                </foreignObject>

                {/* Load-children button on the right side of the card */}
                {nodeDatum._hasUnloaded && (
                    <g
                        transform={`translate(${HALF_W + 1}, 10)`}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); clicJobTreeRef.current(nodeDatum._id); }}
                    >
                        <circle r={11} fill="#59b0b8" style={{ stroke: '#59b0b8', strokeWidth: 2 }} />
                        <text
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="white"
                            fontSize="16"
                            fontWeight="bold"
                            style={{ userSelect: 'none', stroke: 'white', strokeWidth: 1 }}
                        >
                            +
                        </text>
                    </g>
                )}
            </g>
        );
    }, []);

    return (
        <ProtectedRoute>
            <Tooltip target=".showTooltip" />

            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <div className='col text-bg-green p-3'>
                        <a href='/painel/' className="fs-6 fw-bold ps-5 text-decoration-none text-white">
                            <FontAwesomeIcon icon={solid('arrow-left')} /> Organograma
                        </a>
                    </div>
                </div>

                <div className='d-flex-auto my-4 mx-5 pt-3 bg-white rounded-top paddingBottom200' style={{ minHeight: '500px' }}>
                    <div className="row m-3 p-3 justify-content-start">
                        <div className='col bg-white pb-5 ps-3'>
                            {pageLoad ? (
                                <SkeletonOrgEmp />
                            ) : (
                                <>
                                    <div className="pt-4 d-flex mb-4">
                                        <div className='col'>
                                            <div className='d-flex'>
                                                <div className='p-inputgroup'>
                                                    <AutoComplete
                                                        inputClassName='input-search-radius'
                                                        placeholder='Buscar pelo Nome'
                                                        value={search}
                                                        suggestions={items.map((i) => i.user)}
                                                        completeMethod={searchItems}
                                                        onChange={(e) => setSearch(e.value)}
                                                    />
                                                    <button className='btn btn-success button-search-radius' onClick={getJobTree}>
                                                        <FontAwesomeIcon icon={solid('magnifying-glass')} />
                                                    </button>
                                                </div>
                                                {search && (
                                                    <button
                                                        className='btn btn-sm btn-link fw-bold font1em text-grey text-decoration-none'
                                                        onClick={cleanSearch}
                                                    >
                                                        Limpar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <hr className='hr-border' />
                                    </div>

                                    {treeData && (
                                        <div
                                            ref={containerRef}
                                            style={{
                                                width: '100%',
                                                height: '640px',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: 10,
                                                background: '#f8fafc',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Tree
                                                data={treeData}
                                                orientation="horizontal"
                                                pathFunc="elbow"
                                                translate={translate}
                                                zoom={0.75}
                                                scaleExtent={{ min: 0.2, max: 2 }}
                                                nodeSize={{ x: 220, y: 130 }}
                                                separation={{ siblings: 1.3, nonSiblings: 1.6 }}
                                                renderCustomNodeElement={renderNode}
                                                collapsible={false}
                                                enableLegacyTransitions={false}
                                                pathClassFunc={() => 'org-tree-link'}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="card flex justify-content-center">
                            <Sidebar
                                className="w-50"
                                visible={showSidebar}
                                position="right"
                                onHide={() => { setShowSidebar(false); setSelection(null); }}
                            >
                                {selection && (
                                    selection._vacant ? (
                                        <div>
                                            <span className="badge bg-warning text-dark mb-2">Vaga</span>
                                            <p className='lead fw-bold text-default mb-1'>A Preencher</p>
                                            {selection._jobposition?.title && (
                                                <p className='text-muted'>{selection._jobposition.title}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <p className='lead fw-bold text-default mb-0'>{selection.name}</p>
                                            {selection._jobposition?.title && (
                                                <p>{selection._jobposition.title}</p>
                                            )}
                                            <div>
                                                <span
                                                    className='btn btn-sm btn-outline-success btn-rounded px-3 m-1'
                                                    role='button'
                                                    onClick={() => window.open(`/career/summary/${selection._id}/`, '_self', 'rel=noopener noreferrer')}
                                                >
                                                    Ver Perfil
                                                </span>
                                            </div>
                                        </div>
                                    )
                                )}
                            </Sidebar>
                        </div>
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
